import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { Surface } from './Surface';

export interface PageHeaderProps {
  /** What this page is — its name, not its category. */
  title: React.ReactNode;
  /** One line on what it holds. Shown under the title when there is one. */
  description?: React.ReactNode;
  /** Small facts about it — counts, freshness. Usually `MetaFacts`. */
  meta?: React.ReactNode;
  /** What you can do here, on the right. */
  actions?: React.ReactNode;
  /** A thin progress line along the bottom edge, 0–100 for a job you can measure. */
  progress?: number;
  loading?: boolean;
  ariaLabel?: string;
}

/**
 * The block at the top of a page: what you are looking at, and what you can do
 * to it.
 *
 * Use it where a page's controls are few. A bar holding two facts and three
 * buttons reads as an empty shelf, because a full-width card implies more
 * inside it than that; naming the page fills it honestly and gives the reader
 * something to orient by. Where the controls are themselves substantial — a
 * date range, filters, freshness — use `PageToolbar`, which is built to hold
 * them and can fold its lower layer away on scroll.
 *
 * The title repeats what a breadcrumb says, deliberately: a breadcrumb is for
 * getting somewhere else, a title for knowing where you are.
 */
export function PageHeader({
  title,
  description,
  meta,
  actions,
  progress,
  loading = false,
  ariaLabel = 'Page header',
}: PageHeaderProps) {
  return (
    <Surface
      component="header"
      aria-label={ariaLabel}
      sx={{ position: 'relative', overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 1,
          px: 2,
          py: 1.5,
        }}
      >
        {/* Takes what the actions leave — but never less than 240px. A long
            description does not push the actions down (the text is sized from
            240px, not from its own length); a narrow screen, or a page squeezed
            by the chat panel, does: the actions then drop onto their own line
            and the title and description get the full width, rather than being
            crushed to a word a line with the title cut to one letter. */}
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography variant="h4" component="h1" noWrap sx={{ minWidth: 0 }}>
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              sx={(t) => ({ mt: 0.25, color: t.tokens.text.secondary })}
            >
              {description}
            </Typography>
          )}
          {meta && <Box sx={{ mt: 0.75 }}>{meta}</Box>}
        </Box>

        {actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              // Level with the title rather than the block, so a wrapped
              // description does not drag the buttons down with it.
              mt: 0.25,
            }}
          >
            {actions}
          </Box>
        )}
      </Box>

      {(loading || progress !== undefined) && (
        <LinearProgress
          variant={progress === undefined ? 'indeterminate' : 'determinate'}
          value={progress}
          sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2 }}
        />
      )}
    </Surface>
  );
}
